<section class="flex h-full min-h-0 w-full flex-col overflow-hidden bg-white text-slate-900">
  <header class="flex shrink-0 items-center gap-3 border-b border-slate-200 p-4">
    <h2 class="!m-0 min-w-0 flex-1 !text-lg !font-semibold">{{ 'prro.refund.title' | translate }}</h2>
    <span
      class="h-4 w-4 shrink-0 rounded-full border-2 border-orange-200 border-t-orange-600 motion-safe:animate-spin"
      [class.invisible]="!loading"
      aria-hidden="true"
    ></span>
    <app-button btnType="icon" [disabled]="!canClose" [ariaLabel]="'close' | translate" (onChangeClick)="requestClose()"
      ><app-icon>close</app-icon></app-button
    >
  </header>
  <div
    class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 [scrollbar-gutter:stable]"
    [attr.aria-busy]="loading"
    [attr.inert]="loading && preview ? '' : null"
  >
    <p class="!m-0 text-sm text-slate-500">{{ 'prro.refund.hint' | translate }}</p>
    @if (loading && !preview) {
      <span class="sr-only" role="status">{{ 'prro.loading' | translate }}</span>
      <div class="flex flex-col gap-4 motion-safe:animate-pulse" aria-hidden="true">
        <div class="h-7 w-3/4 rounded bg-slate-100"></div>
        <div class="flex gap-2">
          <div class="h-10 flex-1 rounded-lg bg-slate-100"></div>
          <div class="h-10 flex-1 rounded-lg bg-slate-100"></div>
        </div>
        @for (item of [0, 1, 2]; track item) {
          <div class="flex h-20 flex-col justify-center gap-2 rounded-lg border border-slate-200 p-3">
            <div class="h-4 w-1/2 rounded bg-slate-100"></div>
            <div class="h-3 w-1/3 rounded bg-slate-100"></div>
          </div>
        }
        <div class="h-6 w-1/3 rounded bg-slate-100"></div>
        <div class="h-10 rounded-lg bg-slate-100"></div>
      </div>
    } @else if (preview; as data) {
      <div class="flex items-center gap-2">
        <p class="!m-0 min-w-0 break-all text-xs text-slate-500">
          {{ 'prro.refund.source' | translate }}: {{ data.source_receipt_number }}
        </p>
        @if (data.source_receipt_url) {
          <a
            class="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded !text-orange-700 !no-underline hover:bg-orange-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500"
            [href]="data.source_receipt_url"
            [title]="'prro.receipt_link' | translate"
            [attr.aria-label]="'prro.receipt_link' | translate"
            target="_blank"
            rel="noopener noreferrer"
          >
            <app-icon class="!flex !h-[18px] !w-[18px] !text-[18px] !leading-none">receipt_long</app-icon>
          </a>
        }
      </div>
      @if (data.pending || submitted) {
        <p class="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">{{ 'prro.refund.pending' | translate }}</p>
        <app-button btnType="stroked" [disabled]="busy" (onChangeClick)="check()">{{
          'prro.refund.check' | translate
        }}</app-button>
      } @else if (data.remaining > 0) {
        <div class="flex flex-wrap gap-2">
          <app-button
            [btnType]="partial ? 'stroked' : 'raised'"
            [disabled]="busy || !!submittedBody"
            (onChangeClick)="partial = false"
            >{{ 'prro.refund.full' | translate }}</app-button
          >
          <app-button
            [btnType]="partial ? 'raised' : 'stroked'"
            [disabled]="busy || !!submittedBody"
            (onChangeClick)="partial = true"
            >{{ 'prro.refund.partial' | translate }}</app-button
          >
        </div>
        @if (data.advance) {
          <p class="!m-0 text-sm text-slate-500">{{ 'prro.refund.advance_hint' | translate }}</p>
          @if (partial) {
            <app-input
              inputType="number"
              [label]="'prro.total' | translate"
              [input]="advanceAmount"
              [min]="0.01"
              [max]="data.remaining"
              [disabled]="busy || !!submittedBody"
              (onValueChange)="advanceAmount = $event"
            />
          }
        } @else {
          @for (row of data.rows; track row.index) {
            @if (row.quantity > 0) {
              <div class="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
                <div class="min-w-0 flex-1">
                  <p class="!m-0 text-sm font-medium">{{ row.name }}</p>
                  <span class="text-xs text-slate-500"
                    >{{ 'prro.refund.available' | translate }}: {{ row.quantity }}</span
                  >
                </div>
                @if (partial) {
                  <app-input
                    class="w-32"
                    inputType="number"
                    [label]="'prro.operation.quantity' | translate"
                    [min]="0"
                    [max]="row.quantity"
                    [integerOnly]="true"
                    [minFractionDigits]="0"
                    [input]="quantities[row.index]"
                    [disabled]="busy || !!submittedBody"
                    (onValueChange)="quantities[row.index] = $event"
                  />
                }
              </div>
            }
          }
        }
        <strong class="text-lg"
          >{{ 'prro.refund.total' | translate }}: {{ total | currency: 'UAH' : 'symbol-narrow' : '1.2-2' }}</strong
        >
        <app-button btnType="raised" [disabled]="busy || (!submittedBody && !valid)" (onChangeClick)="submit()">{{
          (submittedBody ? 'prro.refund.resubmit' : 'prro.refund.submit') | translate
        }}</app-button>
      } @else {
        <p class="text-sm text-slate-500">{{ 'prro.refund.completed' | translate }}</p>
      }
      @for (operation of data.returns; track operation.id) {
        <div class="flex flex-col gap-2 rounded-lg border border-slate-200 p-3 text-sm">
          <span
            >{{ operation.amount | currency: 'UAH' : 'symbol-narrow' : '1.2-2' }} ·
            {{ 'prro.status.' + operation.status | translate }}</span
          >
          @if (operation.receipt_url) {
            <a
              class="text-orange-700 underline"
              [href]="operation.receipt_url"
              target="_blank"
              rel="noopener noreferrer"
              >{{ 'prro.receipt_link' | translate }}</a
            >
          }
          @if (operation.error) {
            <p class="!m-0 text-red-700">{{ notifications.errorMessage(operation.error) }}</p>
          }
          @if (canRetry(operation)) {
            <app-button btnType="stroked" [disabled]="busy" (onChangeClick)="retry(operation)">{{
              'prro.retry' | translate
            }}</app-button>
          }
        </div>
      }
    } @else {
      <app-button btnType="stroked" (onChangeClick)="load()">{{ 'prro.retry' | translate }}</app-button>
    }
  </div>
</section>
